import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Alert, Button, Save01 } from '@opendate-app/setlist';
import SeatingChartDesignerModal from './SeatingChartDesignerModal';
import SeatsInTheWayPanel, { alsoTakenSummary, removedSummary } from './SeatsInTheWayPanel';
import { dismissToast, toastError, toastStickyError, toastStickyWarning, toastSuccess } from '../../../shared/toastHelper';

const SKIPPED_SHOWS_LISTED = 5;

const EditSeatingChartDesigner = ({
  show,
  onClose,
  seatingChart,
  onChartSynced,
  readOnlyName=false,
  onRefused=null,
  onDiscarded=null,
  startWithPanel=false,
  hasPendingDraft=false,
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const refusalToastId = useRef(null);
  const [overCapacity, setOverCapacity] = useState(null);
  const [seatsInTheWay, setSeatsInTheWay] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [refusals, setRefusals] = useState(0);

  const clearRefusal = () => {
    if (refusalToastId.current) dismissToast(refusalToastId.current);
    refusalToastId.current = null;
  };

  useEffect(() => {
    if (show && hasPendingDraft) setHasUnsavedChanges(true);
  }, [show, hasPendingDraft]);

  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    if (show) window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges, show]);


  const syncChart = useCallback(async () => {
    try {
      setIsSaving(true);
      const { data } = await axios.patch(`/seating_charts/${seatingChart.id}/sync`);
      onChartSynced && onChartSynced(data.seating_chart);
      setHasUnsavedChanges(false);
      setIsSaving(false);
      clearRefusal();
      setSeatsInTheWay(null);
      setPanelOpen(false);
      toastSuccess('Chart synced successfully.');
      if (data.skipped_shows?.length) {
        const listed = data.skipped_shows.slice(0, SKIPPED_SHOWS_LISTED);
        const more = data.skipped_shows.length - listed.length;
        refusalToastId.current = toastStickyWarning(`These upcoming shows kept their old seat map. ${listed.join('. ')}.${more ? ` And ${more} more.` : ''}`);
      }
      setOverCapacity(data.over_capacity || null);
      return !data.over_capacity;
    } catch (err) {
      const errors = err?.response?.data?.errors;
      const inTheWay = err?.response?.data?.seats_in_the_way;
      if (inTheWay) {
        clearRefusal();
        setSeatsInTheWay(inTheWay);
        setRefusals((count) => count + 1);
        if (onRefused) onRefused();
        if (errors?.length > 1) refusalToastId.current = toastStickyError(errors.slice(1).join(' '));
      } else if (errors?.length) {
        setSeatsInTheWay(null);
        clearRefusal();
        refusalToastId.current = toastStickyError(errors.join(' '));
      } else {
        toastError('There was an error syncing the chart.');
      }
      setIsSaving(false);
      return false;
    }
  }, [seatingChart?.id, onChartSynced, onRefused]);

  const handleDiscard = useCallback(async () => {
    if (!window.confirm('Throw away every change since this seat map was last saved? This can\'t be undone.')) {
      return;
    }
    try {
      setIsSaving(true);
      const { data } = await axios.post(`/seating_charts/${seatingChart.id}/discard_draft`);
      setHasUnsavedChanges(false);
      setIsSaving(false);
      clearRefusal();
      toastSuccess(data?.discarded ? 'Changes discarded.' : 'Nothing to discard. Your last save is already live.');
      if (onDiscarded) onDiscarded();
      onClose();
    } catch (err) {
      toastError('There was an error discarding your changes.');
      setIsSaving(false);
    }
  }, [seatingChart?.id, onClose, onDiscarded]);

  const handleSaveAndExit = useCallback(async () => {
    if (hasUnsavedChanges && !(await syncChart())) {
      return;
    }
    onClose();
  }, [hasUnsavedChanges, syncChart, onClose]);

  const closeAfterCapacity = useCallback(async (raise) => {
    if (raise) {
      try {
        await axios.post(`/seating_charts/${seatingChart.id}/raise_capacity`);
        toastSuccess(`Show capacity raised to ${overCapacity.seats}.`);
      } catch (err) {
        toastError('There was an error raising the show capacity.');
        return;
      }
    }
    setOverCapacity(null);
    handleSaveAndExit();
  }, [seatingChart?.id, overCapacity, handleSaveAndExit]);

  const refreshSeatsInTheWay = useCallback(async () => {
    try {
      const { data } = await axios.get(`/seating_charts/${seatingChart.id}/seats_in_the_way_report`);
      setSeatsInTheWay(data.seats_in_the_way);
      return true;
    } catch (err) {
      toastError('There was an error checking the seats you removed.');
      return false;
    }
  }, [seatingChart?.id]);

  useEffect(() => {
    if (show && startWithPanel) {
      refreshSeatsInTheWay().then((ok) => { if (ok) { setRefusals((count) => count + 1); setPanelOpen(true); } });
    }
  }, [show, startWithPanel, refreshSeatsInTheWay]);

  useEffect(() => {
    if (!show) {
      setOverCapacity(null);
      setSeatsInTheWay(null);
      setPanelOpen(false);
    }
  }, [show]);

  const inTheWayCount = seatsInTheWay ? seatsInTheWay.sold.length + seatsInTheWay.held.length + seatsInTheWay.killed.length + seatsInTheWay.carts.length + (seatsInTheWay.taken || []).length + (seatsInTheWay.shrunk || []).length + (seatsInTheWay.releasing || []).length : 0;
  const soldCount = seatsInTheWay ? seatsInTheWay.sold.length : 0;
  const alsoTaken = seatsInTheWay ? alsoTakenSummary(seatsInTheWay) : '';

  const seatsInTheWayNotice = seatsInTheWay ? (inTheWayCount > 0 ? (
    <Alert
      color="warning"
      size="full-width"
      role="alert"
      title={soldCount ? `${soldCount} sold ${soldCount === 1 ? 'ticket is' : 'tickets are'} on seats you removed` : 'A few seats you removed are still taken'}
      description={`Move those fans to new seats or refund them, then save again.${alsoTaken ? ` There's also ${alsoTaken}.` : ''} Your changes are kept${seatsInTheWay.removedOffSale ? ', and the removed seats are off sale until you\'re done' : ''}.`}
      actions={(
        <>
          <Button variant="primary" size="sm" onClick={() => setPanelOpen(true)}>Move fans</Button>
          <Button variant="secondary" size="sm" onClick={onClose}>Finish later</Button>
        </>
      )}
    />
  ) : (
    <Alert
      color="success"
      size="full-width"
      title="Everyone's off the seats you removed"
      description={(seatsInTheWay.removedLabels || []).length && seatsInTheWay.removedLabels.every((label) => /-\d+$/.test(label))
        ? `Save to finish. ${removedSummary(seatsInTheWay.removedLabels)} come off the map.`
        : "Save to finish. Those seats come off the map and the section's capacity drops to match."}
      actions={<Button variant="primary" size="sm" icon={Save01} onClick={handleSaveAndExit}>Save and Exit</Button>}
    />
  )) : null;

  return (
    <>
    <SeatingChartDesignerModal
      show={show}
      onSaveAndExit={handleSaveAndExit}
      onDiscard={seatingChart?.event_chart ? handleDiscard : null}
      isSaving={isSaving}
      editorKeyUrl={`/seating_charts/${seatingChart.id}/editor_key`}
      chartKey={seatingChart?.seatsio_id}
      readOnlyName={readOnlyName}
      hasUnsavedChanges={hasUnsavedChanges}
      notice={overCapacity ? (
        <Alert
          color="warning"
          role="alert"
          title={`This puts the show over its capacity of ${overCapacity.capacity}. Raise capacity too?`}
          actions={
            <>
              <Button variant="secondary" size="sm" onClick={() => closeAfterCapacity(false)}>Keep {overCapacity.capacity}</Button>
              <Button variant="primary" size="sm" onClick={() => closeAfterCapacity(true)}>Raise to {overCapacity.seats}</Button>
            </>
          }
        />
      ) : seatsInTheWayNotice}
      setHasUnsavedChanges={setHasUnsavedChanges}
      overlayZIndex={panelOpen ? 990 : 1060}
    />
    {seatsInTheWay ? (
      <SeatsInTheWayPanel
        key={refusals}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        report={seatsInTheWay}
        onRefresh={refreshSeatsInTheWay}
        onSave={handleSaveAndExit}
      />
    ) : null}
    </>
  );
};

EditSeatingChartDesigner.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  seatingChart: PropTypes.object.isRequired,
  onChartSynced: PropTypes.func,
  readOnlyName: PropTypes.bool,
  onRefused: PropTypes.func,
  onDiscarded: PropTypes.func,
  startWithPanel: PropTypes.bool,
  hasPendingDraft: PropTypes.bool,
};

EditSeatingChartDesigner.defaultProps = {
  onChartSynced: null,
  readOnlyName: false,
  onRefused: null,
  onDiscarded: null,
  startWithPanel: false,
  hasPendingDraft: false,
};

export default EditSeatingChartDesigner;


